/**
 * Formats a list of clinical trial phases into a condensed, readable string
 *
 * If all phases follow the "Phase X" pattern (e.g., "Phase 1", "Phase 2"),
 * returns a condensed format like "Phase 1, 2, 3"
 * Otherwise returns the original phases joined with commas
 */
export default function getCondensedPhaseList(phaseList: string[]): string {
  const phaseValues = phaseList.map((phase) =>
    phase === 'Not Applicable' ? 'Unphased' : phase,
  )

  // Check if all phases follow the "Phase X" pattern
  const phaseRegex = /^Phase\s+(\d+|[IV]+)$/
  const allPhases = phaseValues.filter((p) => phaseRegex.test(p))

  if (allPhases.length > 0 && allPhases.length === phaseValues.length) {
    // All items are phases, so we can use the condensed format
    const numbers = allPhases.map((p) => p.replace('Phase ', ''))

    // Sort the numbers - handle both Arabic (1, 2, 3) and Roman (I, II, III) numerals
    const sortedNumbers = [...numbers].sort((a, b) => {
      // Check if both are Arabic numerals
      if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
        return parseInt(a, 10) - parseInt(b, 10)
      }

      // If they're Roman numerals, convert to values for comparison
      // This handles common clinical trial Roman numerals (I-IV)
      const romanToValue: Record<string, number> = {
        I: 1,
        II: 2,
        III: 3,
        IV: 4,
        V: 5,
      }

      return (romanToValue[a] ?? 0) - (romanToValue[b] ?? 0)
    })

    return `Phase ${sortedNumbers.join(', ')}`
  } else {
    // Mixed or special phases, keep the original format
    return phaseValues.join(', ')
  }
}
